import { type DefaultMessagesResponse } from '@post-office/message-maker-browser/types';

import {
	ROVO_CHAT_3P_CONNECTOR_NUDGE_TEMPLATE_ID,
	ROVO_CHAT_LONG_RUNNING_TASK_SPOTLIGHT_TEMPLATE_ID,
	ROVO_EXTENSION_CHAT_INPUT_MESSAGE_TEMPLATE_ID,
	ROVO_MOBILE_DEEP_RESEARCH_CTA_TEMPLATE_ID,
} from './constants';

import type { MessageEligibility } from './types';

type PlacementMessage = DefaultMessagesResponse['messages'][number];

type PlacementMessages = DefaultMessagesResponse['messages'];

/**
 * Filters messages based on a client-side eligibility map.
 *
 * Each entry maps a message template ID to an eligibility check:
 * - `boolean`: static — `false` excludes the message, `true` keeps it
 * - `(message) => boolean`: dynamic — evaluated with the full message object,
 *   allowing decisions based on server-provided context (e.g. TAP trait data
 *   flattened into the message)
 *
 * Messages not present in the map (or when the map is undefined / empty)
 * default to eligible — fully backwards compatible.
 */
export const filterByClientEligibility = (
	messages: PlacementMessages,
	eligibility?: MessageEligibility,
): PlacementMessages => {
	if (!eligibility) {
		return messages;
	}

	return messages.filter((message: PlacementMessage) => {
		const check = eligibility[message.messageTemplateId];
		if (check === undefined) {
			return true;
		}
		return typeof check === 'function' ? check(message) : check;
	});
};

// Lower index = higher priority. See: https://hello.atlassian.net/wiki/spaces/rocx/pages/7020280418
export const PRIORITY_ORDER: readonly string[] = [
	ROVO_EXTENSION_CHAT_INPUT_MESSAGE_TEMPLATE_ID,
	ROVO_CHAT_LONG_RUNNING_TASK_SPOTLIGHT_TEMPLATE_ID,
	ROVO_CHAT_3P_CONNECTOR_NUDGE_TEMPLATE_ID,
	ROVO_MOBILE_DEEP_RESEARCH_CTA_TEMPLATE_ID,
];

/**
 * Selects the single highest-priority message to render when the placement
 * receives messages from multiple templates. Walks `PRIORITY_ORDER` and
 * returns the first match; falls back to `messages[0]` for any template not
 * yet ranked so existing/future templates do not silently disappear.
 */
export const selectHighestPriorityMessage = (messages: PlacementMessages): PlacementMessages => {
	let selected: PlacementMessage | undefined;

	for (const templateId of PRIORITY_ORDER) {
		selected = messages.find((m: PlacementMessage) => m.messageTemplateId === templateId);
		if (selected) {
			break;
		}
	}

	selected = selected ?? messages[0];

	return selected ? [selected] : [];
};
